import React from 'react';
import { useTranslation } from 'react-i18next';
import { parseDecimalInput, formatExchangeRate, formatMoney, formatQtyForDisplay } from '../utils/decimalInput';
import { toValueInCurrency } from '../utils/priceCurrency';

interface DocumentNestedTableFooterProps {
  rows: any[];
  documentSubType?: "internal" | "external";
  headerCurrency?: { currency?: string } | null;
  // true (domestic supplier) → the import-cost inputs are shown but disabled; the server rejects
  // non-zero costs for a domestic supplier (utils/importCostsGuard.js), корисник 2026-10-14.
  supplierDomestic?: boolean | null;
  showImportCosts?: boolean; // Whether to show/count the import-costs section (capabilities[] 'import-costs'); defaults true so existing consumers are unaffected
  transport1?: string;
  transport2?: string;
  customsDuty?: string;
  freightForwarding?: string;
  warehouseFee?: string;
  sanitaryApproval?: string;
  importCostCoefficient?: number | null;
  note?: string;
  headerNote?: string;
  onTransport1Change?: (value: string) => void;
  onTransport2Change?: (value: string) => void;
  onCustomsDutyChange?: (value: string) => void;
  onFreightForwardingChange?: (value: string) => void;
  onWarehouseFeeChange?: (value: string) => void;
  onSanitaryApprovalChange?: (value: string) => void;
  onNoteChange?: (value: string) => void;
  onHeaderNoteChange?: (value: string) => void;
  isEditable?: boolean;
  useProcurement?: boolean;
  onTotalsChange?: (totals: {
    total: number;
    value: number;
    forPayment: number;
    discount: number;
    vat: number;
    inMkd: number;
  }) => void;
}

const DocumentNestedTableFooter: React.FC<DocumentNestedTableFooterProps> = ({
  rows,
  documentSubType,
  headerCurrency,
  supplierDomestic = null,
  showImportCosts = true,
  transport1 = '',
  transport2 = '',
  customsDuty = '',
  freightForwarding = '',
  warehouseFee = '',
  sanitaryApproval = '',
  importCostCoefficient,
  note = '',
  headerNote = '',
  onTransport1Change,
  onTransport2Change,
  onCustomsDutyChange,
  onFreightForwardingChange,
  onWarehouseFeeChange,
  onSanitaryApprovalChange,
  onNoteChange,
  onHeaderNoteChange,
  isEditable = false,
  useProcurement = false,
  onTotalsChange,
}) => {
  const { t } = useTranslation();
  const costsLocked = !isEditable || supplierDomestic === true;

  const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

  const formatNumber = (value: number): string => formatMoney(value);

  // Calculate total import costs
  const totalImportCosts = React.useMemo(() => {
    return parseNumber(transport1) + parseNumber(transport2) + parseNumber(customsDuty)
      + parseNumber(freightForwarding) + parseNumber(warehouseFee) + parseNumber(sanitaryApproval);
  }, [transport1, transport2, customsDuty, freightForwarding, warehouseFee, sanitaryApproval]);

  // Calculate totals from the stored per-line values, so the footer matches the line columns:
  // Вкупно = Σ Вредност (qty × price), Вредност = Σ Вредност (со попуст), Попуст = the difference,
  // ДДВ = За плаќање − Вредност. (Rows have no `price` field — reading it left everything 0.)
  const totals = React.useMemo(() => {
    let totalGross = 0;
    let totalNet = 0;
    let totalForPayment = 0;
    let totalInCurrency = 0;
    let hasCurrencyRows = false;

    rows.forEach(row => {
      if (row.item?._id) {
        const gross = parseNumber(row.priceValue);
        // Internal documents only set priceValue — no discount there.
        const net = row.priceValueDiscount !== undefined && row.priceValueDiscount !== ''
          ? parseNumber(row.priceValueDiscount)
          : gross;
        totalGross += gross;
        totalNet += net;
        totalForPayment += parseNumber(row.forPayment);
        const inCurrency = toValueInCurrency(row.priceInCurrency, net, row.exchangeRate);
        if (inCurrency !== null) {
          totalInCurrency += inCurrency;
          hasCurrencyRows = true;
        }
      }
    });

    // За плаќање = the supplier's invoice only (= Σ line За плаќање). Import costs (царина, шпедиција,
    // превоз…) are paid to OTHER parties, so they never raise what is owed to the supplier; they belong
    // to the stock's procurement value (МСС 2) — shown separately as Набавна вредност
    // (ERP analyst + PANTHEON 1900, 2026-10-14).
    const importCosts = documentSubType === 'external' && showImportCosts ? totalImportCosts : 0;

    return {
      total: totalGross,
      value: totalNet,
      forPayment: totalForPayment,
      discount: totalGross - totalNet,
      vat: totalForPayment - totalNet,
      inMkd: totalForPayment, // Same as forPayment
      inCurrency: totalInCurrency,
      hasCurrencyRows,
      procurementValue: totalNet + importCosts,
    };
  }, [rows, totalImportCosts, documentSubType, showImportCosts]);

  // Reception in a foreign currency: Во {валута} = Σ Вредност во валута (code comes from the header, never hard-coded)
  const currencyCode = (headerCurrency?.currency || '').toUpperCase();
  const showInCurrency = documentSubType === 'external' && !!currencyCode && currencyCode !== 'MKD' && totals.hasCurrencyRows;

  // Calculate total net weight (procurement mode only)
  const totalNetWeight = React.useMemo(() => {
    if (!useProcurement) return 0;
    return rows.reduce((sum, row) => {
      if (!row.item?._id) return sum;
      const nw = parseDecimalInput(row.netWeight) ?? 0;
      const qty = parseDecimalInput(row.quantity) ?? 0;
      return sum + nw * qty;
    }, 0);
  }, [rows, useProcurement]);

  // Expose totals to parent when they change
  React.useEffect(() => {
    if (onTotalsChange) {
      onTotalsChange(totals);
    }
  }, [totals, onTotalsChange]);

  // pr-20: room for the column-manager button (fixed bottom-6 right-6), which otherwise covers „За плаќање“.
  return (
    <div
      className="fixed bottom-0 right-0 z-30 bg-white border border-gray-300 shadow-lg pl-4 pr-20 transition-all duration-300"
      style={{ left: '0' }}
    >
      <div className="flex justify-between items-start p-3">
        {/* Left side - Header note (moved from partner card) + Import costs (only for external documents) */}
        <div className="flex-1 flex flex-col gap-2">
          {onHeaderNoteChange !== undefined && (
            <div className="flex gap-2 items-start">
              <div className="flex flex-col w-80">
                <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                  {t('tableHeaders.documentNote', 'Напомена на документ')}
                </label>
                <textarea
                  value={headerNote}
                  onChange={(e) => onHeaderNoteChange(e.target.value)}
                  disabled={!isEditable}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                  rows={2}
                  placeholder={t('tableHeaders.documentNote', 'Напомена на документ')}
                />
              </div>
            </div>
          )}
          {documentSubType === 'external' && showImportCosts && (
            <div className="flex flex-col gap-2">
              <h3 className="text-xs font-semibold text-gray-700 mb-1">
                {t('procurementCalculation.title') || 'Procurement Calculation'}
              </h3>
              <div className="flex gap-4">
                {/* Transport 1 — Превоз 1 */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.transport1') || 'Transport 1'}
                  </label>
                  <input
                    type="text"
                    value={transport1}
                    onChange={(e) => onTransport1Change?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Transport 2 — Превоз 2 */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.transport2') || 'Transport 2'}
                  </label>
                  <input
                    type="text"
                    value={transport2}
                    onChange={(e) => onTransport2Change?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Customs Duty — Царина */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.customsDuty') || 'Customs Duty'}
                  </label>
                  <input
                    type="text"
                    value={customsDuty}
                    onChange={(e) => onCustomsDutyChange?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Freight Forwarding — Шпедиција */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.freightForwarding') || 'Freight Forwarding'}
                  </label>
                  <input
                    type="text"
                    value={freightForwarding}
                    onChange={(e) => onFreightForwardingChange?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Warehouse Fee — Складишнина */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.warehouseFee') || 'Warehouse Fee'}
                  </label>
                  <input
                    type="text"
                    value={warehouseFee}
                    onChange={(e) => onWarehouseFeeChange?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Sanitary Approval — Санитарна Согласност */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.sanitaryApproval') || 'Sanitary Approval'}
                  </label>
                  <input
                    type="text"
                    value={sanitaryApproval}
                    onChange={(e) => onSanitaryApprovalChange?.(e.target.value)}
                    disabled={costsLocked}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-right"
                    placeholder="0,00"
                  />
                </div>

                {/* Total Import Costs */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.totalImportCosts') || 'Total Import Costs'}
                  </label>
                  <div className="w-28 px-2 py-1 text-sm font-semibold text-teal-700 bg-teal-50 rounded text-right">
                    {formatNumber(totalImportCosts)}
                  </div>
                </div>

                {importCostCoefficient !== undefined && (
                  <div className="flex flex-col">
                    <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                      {t('procurementCalculation.importCostCoefficient') || 'Cost coefficient'}
                    </label>
                    <input
                      type="text"
                      value={formatExchangeRate(importCostCoefficient)}
                      readOnly
                      className="w-28 px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 text-gray-600 cursor-not-allowed text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Note */}
                <div className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase mb-1">
                    {t('procurementCalculation.note') || 'Note'}
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => onNoteChange?.(e.target.value)}
                    disabled={!isEditable}
                    className="w-48 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                    rows={2}
                    placeholder={t('procurementCalculation.note') || 'Note'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Totals: columns, each with primary + secondary value */}
        <div className="flex gap-4">
          {useProcurement && (
            <div className="text-right min-w-[90px]">
              <span className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">
                {t('nestedTable.totalNetWeight') || 'Total Net Wt'}
              </span>
              <span className="text-sm font-semibold text-teal-700">
                {formatQtyForDisplay(totalNetWeight, { showZero: true })}
              </span>
            </div>
          )}
          {/* Вкупно / Попуст */}
          <div className="text-right min-w-[90px]">
            <span className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">
              {t('nestedTable.total') || 'Total'}
            </span>
            <span className="text-sm font-semibold text-gray-800 block">
              {formatNumber(totals.total)}
            </span>
            <span className="text-[10px] font-medium text-gray-400 uppercase block mt-1">
              {t('nestedTable.discount') || 'Discount'}
            </span>
            <span className="text-sm font-semibold text-gray-400 block">
              {formatNumber(totals.discount)}
            </span>
          </div>
          {/* Вредност / ДДВ */}
          <div className="text-right min-w-[90px]">
            <span className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">
              {t('nestedTable.value') || 'Value'}
            </span>
            <span className="text-sm font-semibold text-gray-800 block">
              {formatNumber(totals.value)}
            </span>
            <span className="text-[10px] font-medium text-gray-400 uppercase block mt-1">
              {t('nestedTable.vatTotal') || 'VAT'}
            </span>
            <span className="text-sm font-semibold text-orange-600 block">
              {formatNumber(totals.vat)}
            </span>
          </div>
          {/* Набавна вредност = Вредност + увозни трошоци (only where the costs section is shown) */}
          {documentSubType === 'external' && showImportCosts && (
            <div className="text-right min-w-[100px]">
              <span
                className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5"
                title={t('nestedTable.procurementValueTitle')}
              >
                {t('nestedTable.procurementValue')}
              </span>
              <span className="text-sm font-semibold text-gray-800 block">
                {formatNumber(totals.procurementValue)}
              </span>
            </div>
          )}
          {/* За плаќање / Во МКД */}
          <div className="text-right min-w-[100px]">
            <span className="text-[10px] font-medium text-gray-500 uppercase block mb-0.5">
              {t('nestedTable.forPayment') || 'For Payment'}
            </span>
            <span className="text-sm font-semibold text-blue-600 block">
              {formatNumber(totals.forPayment)}
            </span>
            <span className="text-[10px] font-medium text-gray-400 uppercase block mt-1">
              {showInCurrency ? t('nestedTable.inCurrency', { code: currencyCode }) : (t('nestedTable.inMkd') || 'In MKD')}
            </span>
            <span className="text-sm font-semibold text-green-600 block">
              {formatNumber(showInCurrency ? totals.inCurrency : totals.inMkd)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentNestedTableFooter;
